import { type MouseEvent, useCallback, useState } from 'react';

import { type ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Button } from '@mui/material';
import { Check } from 'lucide-react';

import { CLAIM_LEAD_MUTATION } from '@modules/_shared/components/feed/Activity/Assignment';
import { GET_PENDING_ACTIVITIES } from '@modules/_shared/components/feed/utils/activityQueries';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import {
  GET_DASHBOARD_NEW_LEADS,
  GET_DASHBOARD_TASKS,
} from '@modules/dashboard/dashboardQueries';
import { GET_LEAD_AGENT_STATUS } from '@modules/lead-details/components/cards/LeadDetailsStatusCard';
import { GET_LEAD_DETAILS } from '@modules/lead-details/LeadDetailsDrawer';
import { GET_LEADS, GET_LEAD_AGENTS } from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';

type ClaimLeadButtonProps = {
  leadId: string | null;
  claimForUserId: string | null;
  onSuccess?: () => void;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
};

export const ClaimLeadButton = ({
  leadId,
  claimForUserId,
  onSuccess,
  color = 'primary',
  size,
  disabled = false,
}: ClaimLeadButtonProps) => {
  const { t } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [claimLead, { loading: isClaiming }] = useMutation(
    CLAIM_LEAD_MUTATION,
    {
      onCompleted: () => {
        emitFeedRefresh();
        onSuccess?.();
      },
      onError: setErrorLike,
      refetchQueries: [
        GET_LEADS,
        GET_LEAD_DETAILS,
        GET_LEAD_AGENT_STATUS,
        GET_LEAD_AGENTS,
        GET_PENDING_ACTIVITIES,
        GET_DASHBOARD_TASKS,
        GET_DASHBOARD_NEW_LEADS,
      ],
    },
  );

  const handleClaimClick = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (leadId == null) {
        console.error('Lead ID is required');
        return;
      }

      if (claimForUserId == null) {
        console.error('Claim for user ID is required');
        return;
      }

      await claimLead({
        variables: {
          lead_id: leadId,
          user_id: claimForUserId,
        },
      });
    },
    [claimLead, claimForUserId, leadId],
  );

  return (
    <>
      <Button
        variant="soft"
        color={color}
        size={size}
        startIcon={
          <Check size={size === 'large' ? 18 : size === 'small' ? 14 : 16} />
        }
        loading={isClaiming}
        loadingPosition="start"
        disabled={disabled || !claimForUserId || !leadId}
        onClick={handleClaimClick}
      >
        {t('Claim')}
      </Button>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
